import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, Grid,
  IconButton, Tooltip, Divider, Avatar, CircularProgress, Input,
  Switch, FormControlLabel,
} from '@mui/material';
import { Business, Home, Save, CloudUpload } from '@mui/icons-material';
import axios from 'axios';
import useCompanyLogo from '../hooks/useCompanyLogo';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const Field = ({ label, k, form, set, ...rest }) => (
  <TextField label={label} value={form[k] || ''} onChange={e => set(k, e.target.value)} fullWidth size="small" {...rest} />
);

const CompanyDetailsPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const logoSrc = useCompanyLogo(form);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/company`, { headers: hdrs() }); setForm(r.data || {}); }
    catch (e) { setError(e.response?.data?.error || 'Failed to load company details'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/api/company/logo/upload`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' },
      });
      setForm(r.data);
      setMsg('Logo uploaded successfully'); setTimeout(() => setMsg(''), 3000);
    } catch (e) { setError(e.response?.data?.error || 'Logo upload failed'); }
    finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await axios.put(`${API}/api/company`, form, { headers: hdrs() });
      setForm(r.data);
      setMsg('Company details saved'); setTimeout(() => setMsg(''), 3000);
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box textAlign="center" mt={6}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Business color="primary" />
              <Typography variant="h5" color="primary">Company Details</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Setups"><IconButton onClick={() => navigate('/setups')}><Home /></IconButton></Tooltip>
              <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            These details appear on your printed documents — certificates, invoices, receipts, payslips and statements.
          </Typography>

          {msg   && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
          {error && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {/* Identity */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar src={logoSrc} variant="rounded" sx={{ width: 64, height: 64, bgcolor: '#eceff1' }}>
              <Business sx={{ color: '#90a4ae' }} />
            </Avatar>
            <Box flex={1}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>Identity</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><Field label="School / Company Name" k="name" form={form} set={set} /></Grid>
                <Grid item xs={12} sm={6}><Field label="Name (line 2)" k="name2" form={form} set={set} /></Grid>
              </Grid>
            </Box>
          </Box>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={8}><Field label="Motto" k="motto" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={4}><Field label="Brand Color" k="brandColor" form={form} set={set} placeholder="#1565c0" /></Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <Box flex={1}>
                  <Input
                    type="file" accept="image/*" onChange={handleLogoUpload}
                    disabled={saving}
                    inputProps={{ 'aria-label': 'Upload logo' }}
                    startAdornment={<CloudUpload sx={{ mr: 1, color: '#90a4ae' }} />}
                    fullWidth
                    placeholder="Click to upload logo (max 2 MB)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    Upload a PNG, JPG, or GIF image (max 2 MB). Stored as a file on the server.
                    {form.logoFileName && ` Current: ${form.logoFileName}`}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Registration</Typography>
          <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
            <Grid item xs={12} sm={6}><Field label="Registration No." k="registrationNo" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Revenue Authority No." k="kraPin" form={form} set={set} /></Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Address</Typography>
          <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
            <Grid item xs={12} sm={8}><Field label="Physical Address" k="address" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={4}><Field label="Address (line 2)" k="address2" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={3}><Field label="City / Town" k="city" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={3}><Field label="Postal Code" k="postalCode" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={3}><Field label="County" k="county" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={3}><Field label="Sub-County" k="subCounty" form={form} set={set} /></Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Contact</Typography>
          <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
            <Grid item xs={12} sm={4}><Field label="Phone" k="phone" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={4}><Field label="Phone 2" k="phone2" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={4}><Field label="Email" k="email" form={form} set={set} type="email" /></Grid>
            <Grid item xs={12} sm={6}><Field label="Website" k="website" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Contact Person" k="contactPerson" form={form} set={set} /></Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Bank / Payment</Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}><Field label="Bank Name" k="bankName" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Bank Branch" k="bankBranch" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Bank Account No." k="bankAccountNo" form={form} set={set} /></Grid>
            <Grid item xs={12} sm={6}><Field label="SWIFT Code" k="swiftCode" form={form} set={set} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary" fontWeight={700}>Parent Portal</Typography>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={!!form.pathwayGuidanceParentVisible}
                onChange={e => set('pathwayGuidanceParentVisible', e.target.checked)} />}
              label="Show AI career-pathway guidance to parents"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              When on, a learner's parents can see career-pathway guidance in their portal — but only
              once a teacher has approved it (marked Reviewed). Drafts are never shown to parents.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyDetailsPage;
